/*----------------------------------
- HOOK: GITHUB INTENT ON THE TLD PAGES
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @rule Read in the browser, on mount, never in `data`: the hub and the insight pages are served
 *       from the static cache whatever the query, so only the visitor's own URL carries the
 *       intent (D14).
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect, useRef } from 'preact/hooks';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import type { WebsiteConversionDatasetContext } from '@uniquedomains/components/conversion/authIntent';

import useContext from '@/client/context';
import {
    resolveGithubIntentOutcome,
    resolveGithubIntentProductAuthentication,
    type GithubIntent,
} from '@/client/pages/landing/domains/page/lib/githubIntent';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { readProductSession } from '@/client/services/conversion/product/productSession';
import { openWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { toAbsoluteProductUrlWithOrigin } from '@/client/utils/payment/productUrls';
import { PRODUCT_PAGE_ROUTES } from '@/common/catalogs/appRoutesCatalog';
import { appErrorReporter } from '@/common/libs/errorReporting';

import { buildTldProductSearchPath } from './tldRadarHref';

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * The public listing's GitHub intent triggers: the dataset links that carry the intent were
 * minted for `/domains/tld/<slug>`, which now 301s to a TLD page with its query, so it is the
 * same visit and keeps its trigger ids.
 */
const resolveGithubIntentTriggerId = (intent: GithubIntent, isLoggedIn: boolean): ConversionTriggerId => {
    if (isLoggedIn) {
        return intent === 'project'
            ? 'website_domains_github_intent_project_logged_in'
            : 'website_domains_github_intent_radar_logged_in';
    }

    return intent === 'project'
        ? 'website_domains_github_intent_project_anonymous'
        : 'website_domains_github_intent_radar_anonymous';
};

/** `https://github.com/<owner>/<name>`, the only form Product sends as `githubRepoUrl`. */
const GITHUB_REPO_URL_PATTERN = /^https:\/\/github\.com\/([\w.-]+)\/([\w.-]+)\/?$/;

/**
 * The dataset context the listing sent for a GitHub visit (`Turn <owner>/<repo> into a Radar`),
 * read from the TLD's repository URL. Undefined when the TLD has no repository or the URL names
 * none: the modal then opens with its plain signup headline.
 */
const buildGithubDatasetContext = (githubRepoUrl: string | null): WebsiteConversionDatasetContext | undefined => {
    const [, owner, name] = githubRepoUrl?.match(GITHUB_REPO_URL_PATTERN) ?? [];
    return owner && name ? { kind: 'dataset', repo: `${owner}/${name}` } : undefined;
};

/**
 * Where the new member lands in Product. An insight page returns to its TLD's Browse search, as
 * the listing did. The hub (`slug` null) receives the retired spokes of every TLD outside the
 * public set, and the D8 301 keeps the query but not the token, so the TLD is unknown there:
 * the plain Browse search is the closest place the Radar can still be created.
 */
const resolveProductSearchPath = (slug: string | null): string =>
    slug ? buildTldProductSearchPath(slug) : PRODUCT_PAGE_ROUTES.domains;

/*----------------------------------
- HOOK
----------------------------------*/

/**
 * A GitHub dataset link (`?github_intent=radar|project`) that lands on a TLD page opens the
 * signup the listing used to open: same intent, same Product search to return to, same dataset
 * headline naming the repository when the page knows it, and the intent is stripped from the
 * address bar so a reload does not repeat it. `githubIntent.ts` is reused as is: its `project`
 * ids are persisted (ADR-0051). `slug` and `githubRepoUrl` are null on the hub.
 */
export const useTldGithubIntent = (slug: string | null, githubRepoUrl: string | null): void => {
    const { publicOrigins, user } = useContext();
    const handledRef = useRef(false);
    const isLoggedIn = Boolean(user?.email);

    useEffect(() => {
        if (handledRef.current || typeof window === 'undefined') return;

        const outcome = resolveGithubIntentOutcome({
            inputUrl: window.location.href,
            productSearchUrl: toAbsoluteProductUrlWithOrigin(resolveProductSearchPath(slug), publicOrigins.productOrigin),
            context: buildGithubDatasetContext(githubRepoUrl),
        });
        if (!outcome) return;

        handledRef.current = true;
        window.history.replaceState(null, '', outcome.cleanedUrl);

        const continueGithubIntent = async () => {
            let productSessionIsAuthenticated = isLoggedIn;
            try {
                productSessionIsAuthenticated = await resolveGithubIntentProductAuthentication(
                    isLoggedIn,
                    () => readProductSession(publicOrigins.productOrigin),
                );
            } catch (sessionError) {
                appErrorReporter.app.handleError(sessionError);
                console.warn('[github-intent] Product session classification failed, continuing anonymously.', sessionError);
            }

            trackConversionTrigger(resolveGithubIntentTriggerId(outcome.intent, productSessionIsAuthenticated), {
                action: outcome.desired.action || null,
                feature: outcome.desired.feature || null,
                href: outcome.href,
                interaction: 'auto',
                page: outcome.desired.page || null,
                section: 'github_intent',
            });
            openWebsiteAuthHref(outcome.href);
        };

        void continueGithubIntent();
    }, [githubRepoUrl, isLoggedIn, publicOrigins.productOrigin, slug]);
};
